import { useCallback, useEffect, useRef, useState } from 'react';
import {
  sendMilliMessage,
  loadConversationHistory,
  resolveSessionId,
  getStoredSessionId,
  storeSessionId,
  listConversations,
  createConversation,
  renameConversation,
  setConversationBusiness,
  archiveConversation,
  touchConversation,
  filesToAttachments,
  MAX_ATTACHMENTS,
} from '@/lib/milliData';
import { arrivedFromShare, clearShareFlag, takeSharedPayload } from '@/lib/shareInbox';

/* useMilliChat — one conversation with Milli, wherever it is drawn.
 *
 * Holds the transcript, the rooms list, attachments and the send loop that
 * the console (and anything else that shows her) render. Everything runs
 * through milli-chat, the same audited orchestrator the dock and the phone
 * use; this hook owns no UI. Server transcript is the truth: on mount and
 * whenever the page comes back into view it asks which room the owner
 * touched last and shows that one, so a question asked on the laptop is
 * what the phone opens to.
 *
 * `onTurn` fires after every reply (her turn may have opened runs, tasks
 * or approvals, so the caller refreshes its panels). */

// Set (as a timestamp) when the paperclip opens the file picker, cleared
// when the pick comes back. Survives the browser reloading the page, which
// is the point: Android low on memory kills the page while the picker is
// open, then reloads it on return. The reload looks identical (transcript
// rehydrates from the server) but the pick died with the old page.
const PICKING_KEY = 'milli_picking_at';

const LOST_PICK_TEXT =
  'The browser ran out of memory and reloaded this page while the file ' +
  'picker was open, so the pick was lost. Close some other apps/tabs and ' +
  'try again. If it keeps happening, open this page in Chrome instead.';

export default function useMilliChat({ onTurn, sendErrorText = "Couldn't reach Milli." } = {}) {
  const [messages, setMessages] = useState([]); // {role:'user'|'milli'|'error', text, department?, atts?}
  // Which desk answered last: {id, name, pillar, uiColor, hex, secondary}.
  const [department, setDepartment] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => getStoredSessionId());
  const [rooms, setRooms] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [attachments, setAttachments] = useState([]); // {media_type,data,name}
  const [attaching, setAttaching] = useState(false);
  // Text that arrived with a share (a caption, a link); the composer takes
  // it once with takeSharedText().
  const [sharedText, setSharedText] = useState('');

  // Mirrors for the sync routine, which runs from window events and would
  // otherwise see stale closures.
  const thinkingRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  const onTurnRef = useRef(onTurn);
  useEffect(() => {
    thinkingRef.current = thinking;
  }, [thinking]);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  useEffect(() => {
    onTurnRef.current = onTurn;
  }, [onTurn]);

  const pushError = useCallback((text) => {
    setMessages((cur) => [...cur, { role: 'error', text }]);
  }, []);

  // Share to Milli: another app POSTed a photo or PDF to /milli/share and
  // the service worker parked it (lib/shareInbox.js). Attach it as if it
  // had come through the paperclip, once, then clean the address bar.
  useEffect(() => {
    if (!arrivedFromShare()) return;
    let cancelled = false;
    (async () => {
      setAttaching(true);
      try {
        const shared = await takeSharedPayload();
        if (cancelled || !shared) return;
        if (shared.files.length) {
          const { attachments: got, errors } = await filesToAttachments(shared.files, MAX_ATTACHMENTS);
          if (cancelled) return;
          if (got.length) setAttachments((cur) => [...cur, ...got].slice(0, MAX_ATTACHMENTS));
          for (const text of errors) pushError(text);
        }
        if (shared.text) setSharedText(shared.text);
      } finally {
        if (!cancelled) setAttaching(false);
        clearShareFlag();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pushError]);

  const takeSharedText = useCallback(() => {
    const t = sharedText;
    if (t) setSharedText('');
    return t;
  }, [sharedText]);

  const loadRooms = useCallback(
    async (includeArchived = showArchived) => {
      try {
        setRooms(await listConversations(includeArchived));
      } catch {
        /* the list just shows what it last had */
      }
    },
    [showArchived]
  );

  // The last department in a rehydrated transcript colors the room: the
  // stored messages do not carry it, so the accent starts from copper and
  // takes the desk color on the first live reply.
  const syncConversation = useCallback(async () => {
    if (thinkingRef.current) return;
    try {
      const id = await resolveSessionId();
      if (!id) return;
      const turns = await loadConversationHistory(id);
      // Her reply may have started arriving on this device while we asked.
      if (thinkingRef.current) return;
      // Switched rooms server-side (other device, or a fresh room): drop the
      // old transcript rather than stacking two rooms' turns. Same room:
      // keep only this device's own error rows, at the bottom.
      if (sessionIdRef.current !== id) {
        setMessages(turns);
        setDepartment(null);
      } else if (turns.length) {
        setMessages((prev) => [...turns, ...prev.filter((m) => m.role === 'error')]);
      }
      setSessionId(id);
      loadRooms();
    } catch {
      /* keep what's on screen */
    } finally {
      setHistoryLoading(false);
    }
  }, [loadRooms]);

  useEffect(() => {
    syncConversation();
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncConversation();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [syncConversation]);

  // Tripwire for the lost file pick (see PICKING_KEY).
  useEffect(() => {
    try {
      const at = Number(sessionStorage.getItem(PICKING_KEY) || 0);
      sessionStorage.removeItem(PICKING_KEY);
      if (at && Date.now() - at < 3 * 60 * 1000) pushError(LOST_PICK_TEXT);
    } catch {
      /* private mode etc. */
    }
    // Cancelling the picker fires no change event, which would leave the
    // flag armed; clear it shortly after focus returns (a real pick's
    // change event lands well inside the grace period).
    const disarm = () => {
      if (document.visibilityState === 'hidden') return; // going INTO the picker
      setTimeout(() => {
        try {
          sessionStorage.removeItem(PICKING_KEY);
        } catch {
          /* ignore */
        }
      }, 5000);
    };
    window.addEventListener('focus', disarm);
    document.addEventListener('visibilitychange', disarm);
    return () => {
      window.removeEventListener('focus', disarm);
      document.removeEventListener('visibilitychange', disarm);
    };
  }, [pushError]);

  /** Call right before opening the file picker. */
  const armPicker = () => {
    try {
      sessionStorage.setItem(PICKING_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  /** onChange handler for the hidden <input type="file">. */
  const pickFiles = async (e) => {
    try {
      sessionStorage.removeItem(PICKING_KEY); // the pick made it back alive
    } catch {
      /* ignore */
    }
    // Copy the list BEFORE resetting the input: Chromium empties the live
    // FileList in place when the value is cleared, so reading it afterwards
    // saw zero files and blamed the phone (2026-09-17 to 2026-09-27).
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setAttaching(true);
    try {
      const { attachments: got, errors } = await filesToAttachments(
        files,
        MAX_ATTACHMENTS - attachments.length
      );
      if (got.length) setAttachments((cur) => [...cur, ...got].slice(0, MAX_ATTACHMENTS));
      for (const text of errors) pushError(text);
    } finally {
      setAttaching(false);
    }
  };

  const removeAttachment = (index) => setAttachments((cur) => cur.filter((_, j) => j !== index));

  /** Send one message (text and/or the pending attachments). */
  const send = async (rawText) => {
    const text = (rawText ?? '').trim();
    if ((!text && attachments.length === 0) || thinking) return false;
    const atts = attachments;
    setAttachments([]);
    setMessages((cur) => [
      ...cur,
      { role: 'user', text: text || '(attachment)', atts: atts.map((a) => a.name) },
    ]);
    setThinking(true);
    try {
      const res = await sendMilliMessage(text, sessionId, atts);
      if (res.session_id && res.session_id !== sessionId) {
        setSessionId(res.session_id);
        storeSessionId(res.session_id);
      }
      setMessages((cur) => [...cur, { role: 'milli', text: res.reply, department: res.department ?? null }]);
      setDepartment(res.department ?? null);
      onTurnRef.current?.(res);
      loadRooms(); // a first message names the room; any message reorders the list
      return true;
    } catch (e) {
      pushError(e?.message || sendErrorText);
      return false;
    } finally {
      setThinking(false);
    }
  };

  // --- Rooms ---
  const currentRoom = rooms.find((r) => r.session_id === sessionId) ?? null;

  const switchRoom = async (id) => {
    if (!id || id === sessionId || thinking) return;
    setSessionId(id);
    storeSessionId(id);
    setMessages([]);
    setDepartment(null);
    setHistoryLoading(true);
    touchConversation(id); // so the phone opens to this one too
    try {
      setMessages(await loadConversationHistory(id));
    } finally {
      setHistoryLoading(false);
    }
  };

  const createRoom = async (title, business) => {
    try {
      const id = await createConversation((title || '').trim(), business || null);
      setSessionId(id);
      setMessages([]);
      setDepartment(null);
      setHistoryLoading(false);
      await loadRooms();
      return id;
    } catch (e) {
      pushError(e?.message || sendErrorText);
      return null;
    }
  };

  const renameRoom = async (title) => {
    if (!sessionId) return false;
    try {
      await renameConversation(sessionId, (title || '').trim());
      await loadRooms();
      return true;
    } catch (e) {
      pushError(e?.message || sendErrorText);
      return false;
    }
  };

  const changeBusiness = async (value) => {
    if (!sessionId) return;
    try {
      await setConversationBusiness(sessionId, value || null);
      await loadRooms();
    } catch (e) {
      pushError(e?.message || sendErrorText);
    }
  };

  const toggleArchive = async () => {
    if (!sessionId) return;
    const archiving = !currentRoom?.archived_at;
    try {
      await archiveConversation(sessionId, archiving);
      const list = await listConversations(showArchived);
      setRooms(list);
      if (archiving) {
        // Move to the newest live room, if there is one.
        const next = list.find((r) => !r.archived_at && r.session_id !== sessionId);
        if (next) await switchRoom(next.session_id);
      }
    } catch (e) {
      pushError(e?.message || sendErrorText);
    }
  };

  const toggleShowArchived = async () => {
    const next = !showArchived;
    setShowArchived(next);
    await loadRooms(next);
  };

  return {
    // transcript
    messages,
    department,
    thinking,
    historyLoading,
    send,
    pushError,
    // attachments
    attachments,
    attaching,
    armPicker,
    pickFiles,
    removeAttachment,
    sharedText,
    takeSharedText,
    // rooms
    sessionId,
    rooms,
    currentRoom,
    showArchived,
    switchRoom,
    createRoom,
    renameRoom,
    changeBusiness,
    toggleArchive,
    toggleShowArchived,
    loadRooms,
  };
}
